import { useMemo } from "react";
import PipelineSummary from "src/business-components/PipelineSummary/PipelineSummary";
import { CopyText } from "src/components/features/small/CopyText/CopyText";
import WhiteTooltip from "src/components/general/WhiteTooltip/WhiteTooltip";
import { useBuildLink } from "src/hooks/useBuildLink";
import { useDebugMode } from "src/hooks/useDebugMode";
import { useQueryPipelineDetails } from "src/queries/pipelines/pipeline.query";
import type { AppPath } from "src/utils/appRoutes/appRoutes.types";
import { PipelineIcon } from "@ui-icons";
import BreadcrumbLink from "../BreadcrumbLink/BreadcrumbLink";

interface BreadcrumbPipelineLinkProps {
  env: string;
  app_name: string;
}

const BreadcrumbPipelineLink = ({ env, app_name }: BreadcrumbPipelineLinkProps) => {
  const buildLink = useBuildLink();
  const isDebugMode = useDebugMode();
  const pipelineQuery = useQueryPipelineDetails({ enabled: isDebugMode });

  const toUrl = buildLink({
    app_name,
    search: window.location.search,
  } as AppPath);

  const tooltipContent = useMemo(
    () => (
      <PipelineSummary
        env_name={env}
        appName={app_name}
        assetType="pipeline"
        label={app_name}
      />
    ),
    [env, app_name]
  );

  return (
    <div className="flex items-center gap-2">
      <BreadcrumbLink
        data-test-id="breadcrumb-pipeline"
        to={toUrl}
        startAdornment={PipelineIcon}
        tooltip={tooltipContent}
        TooltipComponent={WhiteTooltip}
        slotProps={{
          tooltip: {
            force: true,
            enterDelay: 500,
            enterNextDelay: 500,
          },
        }}
      >
        <div className="truncate">{app_name}</div>
      </BreadcrumbLink>
      {isDebugMode && pipelineQuery.data?.app_id != null && (
        <CopyText
          text={String(pipelineQuery.data.app_id)}
          phContext="pipeline_app_id"
        />
      )}
    </div>
  );
};

export default BreadcrumbPipelineLink;
